import html2pdf from "html2pdf.js/dist/html2pdf.bundle.min.js";
import { jsPDF } from "jspdf";
import { GetProductsToOrder } from "../server/ProductToOrder";
import {
  buildOrderHistoryHtmlChunks,
  buildOrdersHistoryCoverHtml,
} from "../components/routs/history/OrdersBackupPdfTemplate";
import { getStatusText } from "./orderStatus";
import { getPaymentStatusText } from "./paymentStatus";

const ITEM_FETCH_CONCURRENCY = 8;
/** How many upcoming orders to prefetch while rendering the current one. */
const PREFETCH_AHEAD = 4;

/**
 * Soft cap for PDF export. Each order needs a network fetch + html2canvas paint;
 * large ranges become very slow and heavy in the browser.
 */
export const MAX_ORDERS_PDF_EXPORT = 40;

const normalizeOrderItems = (payload) => {
  const productsArray = Array.isArray(payload)
    ? payload
    : payload?.orderItems || [];

  return productsArray.map((item) => ({
    ...item,
    name: item.product?.name || item.name || "מוצר לא נמצא",
    quantity: item.quantity,
    collected: item.collected,
    totalPrice:
      item.totalPrice ??
      item.finalTotalPrice ??
      (item.price != null && item.quantity != null
        ? item.price * item.quantity
        : undefined),
    price: item.price ?? item.unitPrice,
    productId: item.productId,
    gtin: item.product?.gtin,
    comment: item.comment || item.Comment || "",
    status: item.status,
    promoId: item.promoId,
    finalTotalPrice: item.finalTotalPrice,
    apsPriceSnapshot: item.apsPriceSnapshot,
    product: item.product,
  }));
};

const enrichOneOrder = async (order, branch) => {
  try {
    const response = await GetProductsToOrder(order.id, branch);
    return {
      ...order,
      orderItems: normalizeOrderItems(response.data),
    };
  } catch (err) {
    console.error("Failed to load items for order", order.id, err);
    return {
      ...order,
      orderItems: Array.isArray(order.orderItems)
        ? normalizeOrderItems(order.orderItems)
        : [],
      _itemsLoadError: true,
    };
  }
};

const formatDisplayDate = (date) => {
  if (date instanceof Date && !Number.isNaN(date.getTime())) {
    return date.toLocaleDateString("he-IL");
  }
  return String(date || "");
};

const buildFilterSummary = ({
  branch,
  fromDate,
  toDate,
  search,
  filterStatus,
  filterPaymentStatus,
  filterLateOrders,
}) => {
  const fromLabel = formatDisplayDate(fromDate);
  const toLabel = formatDisplayDate(toDate);
  const lines = [
    `טווח תאריכים: ${fromLabel || "—"} עד ${toLabel || "—"}`,
    `תחנה: ${branch || "—"}`,
  ];
  if (search?.trim()) lines.push(`חיפוש: ${search.trim()}`);
  if (filterStatus)
    lines.push(`סטטוס ליקוט: ${getStatusText(filterStatus) || filterStatus}`);
  if (filterPaymentStatus)
    lines.push(
      `סטטוס תשלום: ${getPaymentStatusText(filterPaymentStatus) || filterPaymentStatus}`,
    );
  if (filterLateOrders) lines.push("הזמנות באיחור בלבד");
  return lines;
};

const formatFileDate = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "date";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const MARGIN_MM = 10;

/**
 * Capture one HTML block to a single PDF page.
 * Chunks are pre-sized (by table rows) so we fit the whole block on one page
 * instead of slicing through the middle of a table.
 */
const appendHtmlAsPdfPages = async (pdf, html, { isFirst = false } = {}) => {
  const element = document.createElement("div");
  element.style.width = "190mm";
  element.style.background = "#ffffff";
  element.innerHTML = html;

  const canvas = await html2pdf()
    .set({
      margin: 0,
      html2canvas: {
        scale: 1,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      },
    })
    .from(element)
    .toCanvas()
    .get("canvas");

  if (!canvas || !canvas.width || !canvas.height) {
    throw new Error("PDF_CANVAS_EMPTY");
  }

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const usableWidth = pageWidth - MARGIN_MM * 2;
  const usableHeight = pageHeight - MARGIN_MM * 2;

  let drawWidth = usableWidth;
  let drawHeight = (canvas.height * drawWidth) / canvas.width;

  // Prefer shrinking slightly over cutting mid-table when a chunk overflows a bit.
  if (drawHeight > usableHeight) {
    const fit = usableHeight / drawHeight;
    drawWidth *= fit;
    drawHeight = usableHeight;
  }

  const imgData = canvas.toDataURL("image/jpeg", 0.88);

  if (!isFirst) {
    pdf.addPage();
  }

  pdf.addImage(imgData, "JPEG", MARGIN_MM, MARGIN_MM, drawWidth, drawHeight);
};

/**
 * Downloads an orders-history PDF for the given filtered admin orders.
 * Caller may pass batches of up to MAX_ORDERS_PDF_EXPORT and loop for the rest.
 */
export const exportOrdersBackupPdf = async ({
  orders,
  branch,
  fromDate,
  toDate,
  search = "",
  filterStatus = "",
  filterPaymentStatus = "",
  filterLateOrders = false,
  onProgress,
  partNumber = 1,
  totalParts = 1,
  batchStartIndex = 0,
  displayTotal = null,
}) => {
  if (!Array.isArray(orders) || !orders.length) {
    throw new Error("NO_ORDERS");
  }
  if (!branch) {
    throw new Error("NO_BRANCH");
  }

  const limitedOrders = orders.slice(0, MAX_ORDERS_PDF_EXPORT);
  const overallTotal = displayTotal ?? limitedOrders.length;
  const batchFrom = batchStartIndex + 1;
  const batchTo = batchStartIndex + limitedOrders.length;

  const meta = {
    branch,
    fromDate: formatDisplayDate(fromDate),
    toDate: formatDisplayDate(toDate),
    filterSummary: [
      ...buildFilterSummary({
        branch,
        fromDate,
        toDate,
        search,
        filterStatus,
        filterPaymentStatus,
        filterLateOrders,
      }),
      ...(totalParts > 1
        ? [
            `קבוצה ${partNumber} מתוך ${totalParts} · הזמנות ${batchFrom}–${batchTo} מתוך ${overallTotal}`,
          ]
        : []),
    ],
  };

  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  const partSuffix =
    totalParts > 1
      ? `_part${String(partNumber).padStart(2, "0")}-of-${String(totalParts).padStart(2, "0")}`
      : "";
  const filename = `hamazon-orders-history_${branch}_${formatFileDate(fromDate)}_to_${formatFileDate(toDate)}${partSuffix}_${stamp}.pdf`;

  const pdf = new jsPDF({
    unit: "mm",
    format: "a4",
    orientation: "portrait",
  });

  await appendHtmlAsPdfPages(
    pdf,
    buildOrdersHistoryCoverHtml({ orders: limitedOrders, meta }),
    { isFirst: true },
  );

  /** @type {Map<number, Promise<object>>} */
  const pending = new Map();
  let failedItemLoads = 0;

  const startFetch = (index) => {
    if (index < 0 || index >= limitedOrders.length || pending.has(index))
      return;
    const promise = enrichOneOrder(limitedOrders[index], branch).then(
      (enriched) => {
        if (enriched._itemsLoadError) failedItemLoads += 1;
        return enriched;
      },
    );
    pending.set(index, promise);
  };

  for (let i = 0; i < Math.min(PREFETCH_AHEAD + 1, limitedOrders.length); i++) {
    startFetch(i);
  }

  for (let i = 0; i < limitedOrders.length; i++) {
    startFetch(i);
    for (
      let j = i + 1;
      j <= i + PREFETCH_AHEAD && j < limitedOrders.length;
      j++
    ) {
      if (pending.size < ITEM_FETCH_CONCURRENCY + PREFETCH_AHEAD) {
        startFetch(j);
      }
    }

    const order = await pending.get(i);
    pending.delete(i);

    const htmlChunks = buildOrderHistoryHtmlChunks({
      order,
      index: i,
      totalOrders: limitedOrders.length,
      displayIndex: batchStartIndex + i,
      displayTotal: overallTotal,
    });

    for (let c = 0; c < htmlChunks.length; c++) {
      await appendHtmlAsPdfPages(pdf, htmlChunks[c], {
        isFirst: false,
      });
    }

    if (typeof onProgress === "function") {
      onProgress(i + 1, limitedOrders.length);
    }
  }

  pdf.save(filename);

  return {
    orderCount: limitedOrders.length,
    filename,
    failedItemLoads,
    wasLimited: orders.length > limitedOrders.length,
    totalMatched: overallTotal,
    maxOrders: MAX_ORDERS_PDF_EXPORT,
    partNumber,
    totalParts,
  };
};
